import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import TopNav from "../components/TopNav";
import LiveOverlay from "../components/LiveOverlay";
import TeamLink from "../components/TeamLink";
import { Calendar, Archive, AlertCircle, ArrowLeft, Trophy, TrendingUp, Share2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { countryToFlag } from "../lib/countryFlag";

const fmtKickoff = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AccuracyStrip = ({ accuracy, teamName }) => {
  const { wins, losses, hit_rate_pct, total_settled, window_days } = accuracy || {};
  const hasData = total_settled > 0;
  return (
    <section
      className="rounded-2xl border border-border bg-card p-6 space-y-4"
      data-testid="accuracy-strip"
    >
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow">Our Track Record</span>
          <h2 className="font-display text-2xl font-bold mt-1">
            {teamName} predictions, last {window_days} days
          </h2>
        </div>
        <Trophy className="w-7 h-7 text-foreground/40" />
      </header>

      {hasData ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3" data-testid="acc-wins">
            <div className="text-[10px] uppercase tracking-[0.12em] text-emerald-500 font-semibold">Wins</div>
            <div className="font-display text-3xl font-bold text-emerald-500 font-tabular mt-1">{wins}</div>
          </div>
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 px-4 py-3" data-testid="acc-losses">
            <div className="text-[10px] uppercase tracking-[0.12em] text-rose-500 font-semibold">Losses</div>
            <div className="font-display text-3xl font-bold text-rose-500 font-tabular mt-1">{losses}</div>
          </div>
          <div className="rounded-xl border border-border bg-subtle px-4 py-3" data-testid="acc-hitrate">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Hit Rate</div>
            <div className="font-display text-3xl font-bold font-tabular mt-1">
              {hit_rate_pct != null ? `${hit_rate_pct}%` : "—"}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-subtle px-4 py-3" data-testid="acc-total">
            <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Settled</div>
            <div className="font-display text-3xl font-bold font-tabular mt-1">{total_settled}</div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
          Not enough settled history yet for {teamName}. Predictions will accumulate here as fixtures complete.
        </div>
      )}
    </section>
  );
};

const FixtureRow = ({ match, kind }) => {
  const navigate = useNavigate();
  const home = match?.home_team?.name || "?";
  const away = match?.away_team?.name || "?";
  const league = match?.league?.name;
  const country = match?.league?.country;
  const flag = country ? countryToFlag(country) : "";
  const fid = match?.fixture_id || match?.id;
  const tps = (match?.prediction?.top_predictions || []).slice(0, 3);

  return (
    <article
      className="rounded-xl border border-border bg-card p-4 hover:border-foreground/30 transition-colors"
      data-testid={`team-fixture-${fid}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {flag && <span>{flag}</span>}
          <span className="truncate max-w-[14rem]">{league}</span>
        </div>
        <div className="flex items-center gap-2">
          <LiveOverlay fixtureId={fid} compact />
          {kind === "recent" && (
            <Badge variant="outline" className="text-[10px] font-bold tracking-wider">
              FT
            </Badge>
          )}
          <span className="text-xs text-muted-foreground font-tabular">{fmtKickoff(match.date)}</span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => fid && navigate(`/match/${fid}`)}
        className="w-full text-left grid grid-cols-[1fr_auto_1fr] items-center gap-3 group mb-3"
      >
        <span className="font-display font-semibold text-base truncate group-hover:underline">{home}</span>
        <span className="text-[10px] font-bold text-muted-foreground tracking-[0.16em]">VS</span>
        <span className="font-display font-semibold text-base truncate text-right group-hover:underline">{away}</span>
      </button>

      {tps.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tps.map((p, i) => (
            <span
              key={i}
              className="text-[11px] px-2 py-1 rounded-md border border-border bg-subtle font-tabular"
            >
              <span className="text-muted-foreground mr-1">{p.market_type.replace(/^.*•\s*/, "")}:</span>
              <span className="font-semibold">{p.prediction}</span>
              <span className="ml-1.5 text-foreground/80">{Math.round(p.confidence)}%</span>
            </span>
          ))}
        </div>
      )}
    </article>
  );
};

export default function TeamPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/team/${slug}`, { params: { days_back: 14, days_ahead: 30, accuracy_window_days: 30 } })
      .then((r) => {
        setData(r.data);
        // Update document title for SEO + share previews
        if (r.data?.share?.title) document.title = r.data.share.title;
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
    return () => {
      document.title = "NDAKUBET";
    };
  }, [slug]);

  const onShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: data?.share?.title,
          text: data?.share?.description,
          url,
        });
      } catch {
        /* user cancelled */
      }
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <TopNav />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="team-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {loading && (
          <div className="rounded-2xl border border-border bg-card px-6 py-12 text-center text-muted-foreground" data-testid="team-loading">
            Loading team…
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 px-4 py-4 text-sm text-rose-500 flex items-center gap-2" data-testid="team-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {data && !loading && (
          <>
            <header className="flex items-start justify-between flex-wrap gap-4 nd-fade-up" data-testid="team-hero">
              <div className="flex items-center gap-4 min-w-0">
                {data.team?.logo && (
                  <img
                    src={data.team.logo}
                    alt={data.team.name}
                    className="w-16 h-16 object-contain rounded-2xl border border-border bg-card p-2"
                  />
                )}
                <div className="min-w-0">
                  <span className="eyebrow">Team</span>
                  <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight truncate">
                    {data.team?.name}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">{data.share?.description}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={onShare}
                data-testid="team-share-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border border-border hover:bg-muted transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </header>

            <AccuracyStrip accuracy={data.accuracy} teamName={data.team?.name} />

            <section data-testid="team-upcoming" className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                <Calendar className="w-4 h-4" />
                Upcoming
                <span className="font-tabular text-foreground/80">· {data.upcoming?.length || 0}</span>
              </div>
              {data.upcoming?.length ? (
                <div className="grid gap-3">
                  {data.upcoming.map((m) => (
                    <FixtureRow key={`up-${m.fixture_id || m.id}`} match={m} kind="upcoming" />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
                  No upcoming fixtures scheduled.
                </div>
              )}
            </section>

            <section data-testid="team-recent" className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                <Archive className="w-4 h-4" />
                Recent
                <span className="font-tabular text-foreground/80">· {data.recent?.length || 0}</span>
              </div>
              {data.recent?.length ? (
                <div className="grid gap-3">
                  {data.recent.map((m) => (
                    <FixtureRow key={`re-${m.fixture_id || m.id}`} match={m} kind="recent" />
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground">
                  No fixtures finished in the last window.
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
